import Game from "../../scripts/game/Game";
import Scenes from "../../scripts/enums/Scenes";
import StageType from "../../scripts/enums/StageType";
import { showNarration } from "../../utils/modal/ModalManager";
import {
  TIME_BASED_NARRATIONS,
  NARRATION_ENTRIES,
} from "../../scripts/consts/NARRATIONS";

// Track which time-based narrations have been triggered in the current session
// This prevents triggering the same narration multiple times if time fluctuates
const triggeredTimeNarrations = new Set<string>();

// Store active event timers so they can be cleaned up later
const activeTimers: Phaser.Time.TimerEvent[] = [];

// Store setTimeout IDs for cleanup
const narrationTimeouts: number[] = [];

// Track current narration state for chaining
let currentNarrationId: string | null = null;
let isTransitioning = false;

// Register narrations with the game's existing event system
export const registerNarrationEvents = () => {
  // Try using the SceneManager to access the event system
  if (!Game.Core?.SceneManager) {
    console.error("Game.Core.SceneManager is not initialized");
    return;
  }

  // Clear the triggered narrations set when starting a new game
  triggeredTimeNarrations.clear();

  // Clear any existing timers first
  cleanupNarrationEvents();

  // Register time-based events for the current stage
  const currentStage = Game.Core.PlayerOptions?.SelectedStage;
  if (!currentStage) return;

  // Check if narrations exist for this stage
  const stageNarrations = TIME_BASED_NARRATIONS[currentStage];
  if (!stageNarrations || Object.keys(stageNarrations).length === 0) {
    console.log(
      `No narrations defined for stage ${currentStage}, skipping narration setup`
    );
    return;
  }

  // Register each narration as a stage event
  Object.entries(stageNarrations).forEach(([timeStr, narrationId]) => {
    // Convert half-minute value to milliseconds
    const timeInMinutes = parseFloat(timeStr);
    const timeInMilliseconds = timeInMinutes * 60 * 1000;

    // For time 0, trigger immediately
    if (timeInMinutes === 0) {
      // Small delay to ensure game is fully initialized
      const timeoutId = window.setTimeout(() => {
        const triggerKey = `${currentStage}-${timeInMinutes}`;
        if (triggeredTimeNarrations.has(triggerKey)) return;
        triggeredTimeNarrations.add(triggerKey);
        triggerNarration(narrationId);
      }, 100);

      // Store the timeout ID for cleanup
      narrationTimeouts.push(timeoutId);
    } else {
      // Use the current scene to create a timed event for non-zero times
      const timerEvent = Game.Core.SceneManager.scene
        .get(Scenes.MainScene)
        .time.delayedCall(timeInMilliseconds, () => {
          const triggerKey = `${currentStage}-${timeInMinutes}`;
          if (triggeredTimeNarrations.has(triggerKey)) return;
          triggeredTimeNarrations.add(triggerKey);
          triggerNarration(narrationId);
        });

      // Store the timer event reference for cleanup
      activeTimers.push(timerEvent);
    }
  });
};

// Function to clean up all active narration timers
export const cleanupNarrationEvents = () => {
  // Remove all active timer events
  activeTimers.forEach((timer) => {
    if (timer && !timer.hasDispatched) {
      timer.remove();
    }
  });

  // Clear the array
  activeTimers.length = 0;

  // Clear triggered narrations set
  triggeredTimeNarrations.clear();

  // Clear any active timeouts
  narrationTimeouts.forEach((id) => window.clearTimeout(id));
  narrationTimeouts.length = 0;

  // Reset narration state
  currentNarrationId = null;
  isTransitioning = false;
};

// Expose addTimeout function to let other modules register their timeouts
export const addTimeout = (timeoutId: number): void => {
  narrationTimeouts.push(timeoutId);
};

// Function to trigger a narration by ID
export const triggerNarration = (narrationId: string) => {
  const narrationEntry = NARRATION_ENTRIES[narrationId];

  if (!narrationEntry) {
    console.error(`Narration with ID "${narrationId}" not found`);
    return;
  }

  // If we're already showing this narration, do nothing
  if (currentNarrationId === narrationId && !isTransitioning) return;

  // Set current narration ID
  const previousNarrationId = currentNarrationId;
  currentNarrationId = narrationId;

  // Show the narration using the modal system
  showNarration(
    narrationId,
    narrationEntry,
    (nextId: string | null) => {
      // Handle response selection with chaining support
      if (nextId) {
        // Mark that we're transitioning to prevent conflicts
        isTransitioning = true;

        // Trigger the next narration with a minimal delay
        const timeoutId = window.setTimeout(() => {
          triggerNarration(nextId);
          isTransitioning = false;
        }, 10);

        // Register the timeout for cleanup
        addTimeout(timeoutId);
      } else {
        // No next narration, complete this one
        currentNarrationId = null;
        isTransitioning = false;
      }
    },
    () => {
      // On complete callback
      currentNarrationId = null;
      isTransitioning = false;
    },
    isTransitioning,
    previousNarrationId
  );
};

// Function to trigger narration for specific game events
export const triggerEventNarration = (eventType: string) => {
  if (!Game.Core?.PlayerOptions) return;

  const currentStage = Game.Core.PlayerOptions.SelectedStage;
  const narrationId = `${StageType[currentStage].toLowerCase()}-${eventType}`;

  if (NARRATION_ENTRIES[narrationId]) {
    triggerNarration(narrationId);
  }
};

export default {
  registerNarrationEvents,
  triggerNarration,
  triggerEventNarration,
  cleanupNarrationEvents,
  addTimeout,
};
